import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { EntryContext } from '../../context/EntryContext';
import { useRelatedEntries } from '../../hooks/useRelatedEntries';
import type { CollectionEntry } from '../../types/cms';

const COLLECTION_PREFIX = import.meta.env.VITE_COLLECTION_PREFIX ?? 'c';

const COLS: Record<number, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
};

interface RelatedEntriesBlockProps {
  targetCollection?: string;
  relationField?: string;
  layout?: 'grid' | 'list';
  columns?: 2 | 3 | 4;
  limit?: number;
}

function RelatedEntryCard({ entry, typeSlug }: { entry: CollectionEntry; typeSlug: string }) {
  const imageUrl =
    (entry.dataJson?.imageUrl as string | undefined) ??
    (entry.dataJson?.image as string | undefined) ??
    (entry.dataJson?.cover as string | undefined);

  const path = `/${COLLECTION_PREFIX}/${encodeURIComponent(typeSlug)}/${encodeURIComponent(entry.slug ?? entry.id)}`;

  return (
    <Link
      to={path}
      className="block bg-white border border-zinc-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      {imageUrl && (
        <div className="aspect-video overflow-hidden bg-zinc-100">
          <img src={imageUrl} alt={entry.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4 space-y-1">
        <h3 className="font-semibold text-zinc-900 text-sm leading-snug line-clamp-2">{entry.title}</h3>
        {entry.publishedAt && (
          <p className="text-[10px] text-zinc-400">
            {new Date(entry.publishedAt).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        )}
      </div>
    </Link>
  );
}

function RelatedEntryListItem({ entry, typeSlug }: { entry: CollectionEntry; typeSlug: string }) {
  const path = `/${COLLECTION_PREFIX}/${encodeURIComponent(typeSlug)}/${encodeURIComponent(entry.slug ?? entry.id)}`;
  return (
    <Link
      to={path}
      className="flex items-center gap-3 py-2.5 border-b border-zinc-100 last:border-0 hover:text-zinc-600 transition-colors"
    >
      <span className="flex-1 text-sm font-medium text-zinc-800 truncate">{entry.title}</span>
      {entry.publishedAt && (
        <span className="text-xs text-zinc-400 flex-shrink-0">
          {new Date(entry.publishedAt).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      )}
    </Link>
  );
}

export function RelatedEntriesBlock({ props }: { props: RelatedEntriesBlockProps }) {
  const { targetCollection, relationField, layout = 'grid', columns = 3, limit = 10 } = props;

  // Use the raw context value — returns null when outside EntryContextProvider
  const ctx = useContext(EntryContext);
  const currentEntryId = ctx?.entry?.id;

  const { data: entries, isLoading } = useRelatedEntries(
    targetCollection,
    relationField,
    currentEntryId,
    limit,
  );

  if (!targetCollection || !relationField) return null;

  if (isLoading) {
    return (
      <div className={`grid gap-4 ${COLS[columns] ?? COLS[3]}`}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-32 bg-zinc-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) return null;

  if (layout === 'list') {
    return (
      <div>
        {entries.map((entry) => (
          <RelatedEntryListItem key={entry.id} entry={entry} typeSlug={targetCollection} />
        ))}
      </div>
    );
  }

  return (
    <div className={`grid gap-4 ${COLS[columns] ?? COLS[3]}`}>
      {entries.map((entry) => (
        <RelatedEntryCard key={entry.id} entry={entry} typeSlug={targetCollection} />
      ))}
    </div>
  );
}
